import { useState } from 'react'
import { CircularProgress, Menu, MenuItem } from '@mui/material'
import FreightSnapMark from './FreightSnapMark'

interface Props {
    scanned: boolean
    loading: boolean
    error?: boolean
    disabled?: boolean
    onScan: () => void
    onDetails: () => void
}

const Caret = () => (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
)

const FreightSnapControl = ({scanned, loading, error, disabled, onScan, onDetails}: Props) => {
    const [anchor, setAnchor] = useState<null | HTMLElement>(null)

    const showError = !!error && !scanned && !loading

    const primary = () => {
        if(disabled || loading) return
        scanned ? onDetails() : onScan()
    }

    const fromMenu = (fn: () => void) => () => {
        setAnchor(null)
        if(!disabled) fn()
    }

    const bg = loading ? '#F5F8FA' : showError ? '#FEF4F6' : scanned ? '#fff' : '#F5FAFE'
    const border = loading ? '#EFF2F5' : showError ? '#F3C7D2' : '#D7E6F2'
    const caretColor = showError ? '#D9496E' : '#0072C6'

    const title = showError
        ? 'Scan failed. Click to retry.'
        : scanned
            ? 'Dimensions from FreightSnap. Editing them manually removes the scan.'
            : 'FreightSnap scan'

    return (
        <>
            <span style={{display: 'inline-flex'}}>
                <button
                    type="button"
                    onClick={primary}
                    disabled={disabled || loading}
                    title={title}
                    className="d-flex align-items-center justify-content-center"
                    style={{position: 'relative', width: 34, height: 34, border: `1px solid ${border}`, borderRight: 'none', borderRadius: '7px 0 0 7px', background: bg, cursor: (disabled || loading) ? 'default' : 'pointer'}}
                >
                    {loading
                        ? <CircularProgress style={{height: 17, width: 17, color: '#0072C6'}}/>
                        : <span className="d-flex" style={{opacity: showError ? 0.55 : 1}}><FreightSnapMark size={20}/></span>
                    }
                    {scanned && !loading && (
                        <span style={{position: 'absolute', right: -3, bottom: -3, width: 15, height: 15, borderRadius: '50%', background: '#47BE7D', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                        </span>
                    )}
                    {showError && (
                        <span style={{position: 'absolute', right: -3, bottom: -3, width: 15, height: 15, borderRadius: '50%', background: '#e42e5b', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 800, lineHeight: 1}}>!</span>
                    )}
                </button>
                <button
                    type="button"
                    onClick={(e) => !disabled && setAnchor(e.currentTarget)}
                    disabled={disabled}
                    aria-label="FreightSnap menu"
                    className="d-flex align-items-center justify-content-center"
                    style={{width: 22, height: 34, border: `1px solid ${border}`, borderRadius: '0 7px 7px 0', background: bg, color: caretColor, cursor: disabled ? 'default' : 'pointer'}}
                >
                    <Caret/>
                </button>
            </span>

            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                {/* Scan + Details always, like Primus (Amanda/Joaquin, ticket 31017813): Scan is the
                    one-click scan; Details opens the window to pick a dimensioner or review the scan,
                    available even with a single dimensioner so the user can open it before scanning. */}
                {scanned
                    ? [
                        <MenuItem key="details" onClick={fromMenu(onDetails)}>Details</MenuItem>,
                        <MenuItem key="rescan" onClick={fromMenu(onScan)}>Scan again</MenuItem>,
                    ]
                    : [
                        <MenuItem key="scan" onClick={fromMenu(onScan)}>Scan</MenuItem>,
                        <MenuItem key="details" onClick={fromMenu(onDetails)}>Details</MenuItem>,
                    ]
                }
            </Menu>
        </>
    )
}

export default FreightSnapControl
